import classNames from "classnames";
import { useCallback, useEffect } from "react";
import { BatchStatus, LetterBatch } from "react-migration/domains/sites/typings";
import { formatToCurrency, useTranslation } from "react-migration/lib";
import { dateShortMonthString } from "src/js/util/dates";

import {
  Column,
  Table,
  WithSkeletonLoadingProps,
  withSkeletonLoading,
} from "@landtechnologies/components";

import { DropdownMenu, StatusLabel } from "../components";
import { LetterBatchesTableRecordForm } from "./LetterBatchesTableRecordForm";
import { LetterBatchesTableRecordFormProvider } from "./LetterBatchesTableRecordFormProvider";

export interface LetterBatchesTableProps {
  loading: boolean;
  data: LetterBatch[];
}

export const LetterBatchesTable = withSkeletonLoading(
  ({ hideSkeletonLoading, loading, data }: WithSkeletonLoadingProps & LetterBatchesTableProps) => {
    const { t } = useTranslation();

    useEffect(() => {
      if (!loading) {
        hideSkeletonLoading();
      }
    }, [hideSkeletonLoading, loading]);

    const columns: Column<LetterBatch>[] = [
      {
        label: t("sites.letter_batches.name"),
        render: (row, rowIndex) => {
          return data ? (
            <LetterBatchesTableRecordFormProvider data={row}>
              <LetterBatchesTableRecordForm rowIndex={rowIndex} />
            </LetterBatchesTableRecordFormProvider>
          ) : null;
        },
        grow: true,
        id: "name",
      },
      {
        label: t("sites.letter_batches.status"),
        render: ({ status, updated }) => {
          const date = status === BatchStatus.Cancelled ? dateShortMonthString(updated) : undefined;
          const error = status === BatchStatus.Failed ? "Unknown error" : undefined;
          return <StatusLabel status={status} date={date} error={error} />;
        },
      },
      {
        label: t("sites.letter_batches.created"),
        maxWidth: "250px",
        render: ({ created }) => {
          return dateShortMonthString(created);
        },
      },
      {
        label: t("sites.letter_batches.number_of_letters"),
        rowKey: "lettersCount",
        maxWidth: "250px",
        align: "right",
      },
      {
        label: t("sites.letter_batches.price"),
        align: "right",
        maxWidth: "250px",
        render: ({ price }) => {
          return formatToCurrency({ amount: price, currency: "GBP" });
        },
      },
      {
        render: ({ id, status, letterPdfs, name }) => (
          <DropdownMenu batchId={id} status={status} letterPdfs={letterPdfs} name={name} />
        ),
      },
    ];

    const cellClassName = useCallback(
      (_row: LetterBatch, column: Column<LetterBatch>) =>
        column.id === "name" ? "!atlas-p-0" : undefined,
      []
    );

    return (
      <div
        data-testid="letter-batches-table"
        className={classNames(
          "atlas-bg-white",
          "atlas-p-5",
          "atlas-h-full",
          "atlas-w-full",
          "atlas-grid",
          "atlas-overflow-x-auto"
        )}
      >
        <Table primaryKey={"id"} rows={data} columns={columns} cellClassName={cellClassName} />
      </div>
    );
  },
  {
    showPadding: false,
  }
);
